import TabItem from '../../../@types/tab-item';
import {getCurrentTabIndex, getTabItems, updateCurrentTabIndex, updateTabItems} from '../../../utils/tab-utils';
import {uuid} from '../../../utils/utils';

/**
 * Interface for tab persistence operations
 */
export interface TabPersistence {
    saveTabs(tabs: TabItem[]): void;
    loadTabs(): TabItem[];
    saveActiveIndex(index: number): void;
    loadActiveIndex(): number;
    clearAll(): void;
}

/**
 * Implementation of tab persistence using session storage
 * This wraps the existing utility functions to provide a cleaner interface
 */
export class SessionStorageTabPersistence implements TabPersistence {
    
    /**
     * Save tabs to session storage
     */
    saveTabs(tabs: TabItem[]): void {
        updateTabItems(tabs);
    }
    
    /**
     * Load tabs from session storage.
     * Ensures all tabs have a uid for stable React keys - tabs saved before uid was introduced
     * will get a uid generated on load.
     */
    loadTabs(): TabItem[] {
        const tabs = getTabItems();
        // Ensure all tabs have a uid (for backwards compatibility with tabs saved before uid was added)
        tabs.forEach(tab => {
            if (!tab.uid) {
                tab.uid = uuid();
            }
        });
        return tabs;
    }
    
    /**
     * Save the active tab index to session storage
     */
    saveActiveIndex(index: number): void {
        updateCurrentTabIndex(index);
    }
    
    /**
     * Load the active tab index from session storage
     */
    loadActiveIndex(): number {
        return getCurrentTabIndex();
    }
    
    /**
     * Clear all tab-related data from session storage
     */
    clearAll(): void {
        updateTabItems([]);
        updateCurrentTabIndex(0);
    }
}

/**
 * Factory function to create a tab persistence instance
 * This allows for easy switching between different persistence strategies in the future
 */
export function createTabPersistence(type: 'sessionStorage' = 'sessionStorage'): TabPersistence {
    switch (type) {
        case 'sessionStorage':
            return new SessionStorageTabPersistence();
        default:
            throw new Error(`Unknown persistence type: ${type}`);
    }
}

/**
 * Higher-order function that adds persistence to tab operations
 * This can be used to automatically persist state after certain operations
 */
export function withPersistence<T extends (...args: any[]) => any>(
    operation: T,
    persistence: TabPersistence,
    persistenceType: 'tabs' | 'activeIndex' | 'both' = 'both'
): T {
    return ((...args: Parameters<T>): ReturnType<T> => {
        const result = operation(...args);
        
        // If the result contains tabs, persist them
        if ((persistenceType === 'tabs' || persistenceType === 'both') && result?.tabs) {
            persistence.saveTabs(result.tabs);
        }
        
        // If the result contains an active index, persist it
        if ((persistenceType === 'activeIndex' || persistenceType === 'both') && 
            result?.activeIndex !== undefined) {
            persistence.saveActiveIndex(result.activeIndex);
        }
        
        return result;
    }) as T;
}